import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import DataTableHeadCell from "pages/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "pages/Tables/DataTable/DataTableBodyCell";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  total,
  currentPage,
  changePage,
  onRowClick,
}) {
  const defaultValue = entriesPerPage.defaultValue || 10;
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const [pageIndex, setPageIndex] = useState(currentPage);
  const [pageSize, setPageSize] = useState(defaultValue);

  // 图片预览弹窗状态
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex, pageSize } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setGlobalFilter,
    state: { globalFilter },
  } = tableInstance;

  const setEntriesPerPage = (value) => {
    setPageSize(value);
    setPageIndex(0);
    changePage(0);
  };

  const handlePageChange = (newPageIndex) => {
    setPageIndex(newPageIndex);
    changePage(newPageIndex);
  };

  const handleNextPage = () => {
    if (canNextPage) {
      handlePageChange(pageIndex + 1);
      nextPage();
    }
  };

  const handlePreviousPage = () => {
    if (canPreviousPage) {
      handlePageChange(pageIndex - 1);
      previousPage();
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const renderPagination = (() => {
    const delta = 2;
    const currentPage = pageIndex + 1;
    const items = [];

    items.push(
      <MDPagination
        item
        key="prev"
        onClick={() => handlePageChange(Math.max(0, pageIndex - 1))}
        disabled={pageIndex === 0}
      >
        ‹
      </MDPagination>
    );

    const range = [];
    const l = Math.max(2, currentPage - delta);
    const r = Math.min(totalPages - 1, currentPage + delta);

    range.push(1);
    for (let i = l; i <= r; i++) {
      range.push(i);
    }
    if (totalPages > 1) range.push(totalPages);

    let prev = null;
    for (let i of range) {
      if (prev !== null) {
        if (i - prev === 2) {
          items.push(
            <MDPagination
              item
              key={`between-${prev + 1}`}
              onClick={() => handlePageChange(prev)}
            >
              {prev + 1}
            </MDPagination>
          );
        } else if (i - prev > 2) {
          items.push(
            <MDPagination item disabled key={`dots-${i}`}>
              ...
            </MDPagination>
          );
        }
      }

      items.push(
        <MDPagination
          item
          key={i}
          onClick={() => handlePageChange(i - 1)}
          active={currentPage === i}
        >
          {i}
        </MDPagination>
      );
      prev = i;
    }

    items.push(
      <MDPagination
        item
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages - 1, pageIndex + 1))}
        disabled={pageIndex === totalPages - 1}
      >
        ›
      </MDPagination>
    );

    return items;
  })();

  const [search, setSearch] = useState(globalFilter);

  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  const entriesStart = pageIndex * pageSize + 1;
  const entriesEnd = Math.min(entriesStart + pageSize - 1, total);

  // 打开图片预览
  const handleOpenPreview = (imgUrl) => {
    setPreviewImage(imgUrl);
    setPreviewOpen(true);
  };

  // 关闭图片预览
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewImage("");
  };

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {/* 搜索栏 */}
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {canSearch && (
            <MDBox width="12rem" ml="0">
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}

      {/* 表格主体 */}
      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => (
                <DataTableHeadCell
                  key={idx}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width || "auto"}
                  sorted={isSorted ? (column.isSorted ? (column.isSortedDesc ? "desc" : "asc") : "none") : false}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {data.map((row, key) => {
            return (
              <TableRow key={key} onClick={() => onRowClick(key)}>
                {Object.keys(row).map((columnKey, idx) => {
                  const value = row[columnKey];
                  // 判断是否为图片 URL
                  const isImage = typeof value === "string" && 
                    (value.startsWith("http://") || value.startsWith("https://")) &&
                    (value.endsWith(".png") || value.endsWith(".jpg") || value.endsWith(".jpeg") || value.endsWith(".gif"));

                  return (
                    <DataTableBodyCell key={idx}>
                      {isImage ? (
                        // 表格内缩略图
                        <img
                          src={value}
                          alt="preview"
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // 阻止触发行点击
                            handleOpenPreview(value);
                          }}
                        />
                      ) : (
                        value
                      )}
                    </DataTableBodyCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* 分页 */}
      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && Math.ceil(total / pageSize) <= 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {total} entries
            </MDTypography>
          </MDBox>
        )}
        {Math.ceil(total / pageSize) > 1 && (
          <MDPagination
            variant={pagination.variant || "gradient"}
            color={pagination.color || "info"}
          >
            {canPreviousPage && (
              <MDPagination item onClick={handlePreviousPage}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination}
            {canNextPage && (
              <MDPagination item onClick={handleNextPage}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>

      {/* 🔥 图片预览弹窗（Material-UI Dialog）*/}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
          }
        }}
      >
        <DialogContent sx={{ position: "relative", p: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
          {/* 关闭按钮 */}
          <IconButton
            onClick={handleClosePreview}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.3)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.5)" }
            }}
          >
            <Icon>close</Icon>
          </IconButton>

          {/* 放大图片 */}
          <img
            src={previewImage}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </Dialog>
    </TableContainer>
  );
}

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: false,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  total: 0,
  changePage: () => {},
  onRowClick: () => {},
};

DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary", "secondary", "info", "success", "warning", "error", "dark", "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  total: PropTypes.number.isRequired,
  currentPage: PropTypes.number,
  changePage: PropTypes.func,
  onRowClick: PropTypes.func,
};

export default DataTable;